import React, { useEffect, useState } from "react";
import { Redirect, useHistory } from "react-router-dom";
import PropTypes from "prop-types";
import clsx from "clsx";
import { lighten, makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TablePagination from "@material-ui/core/TablePagination";
import TableRow from "@material-ui/core/TableRow";
import TableSortLabel from "@material-ui/core/TableSortLabel";
import Toolbar from "@material-ui/core/Toolbar";
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import Checkbox from "@material-ui/core/Checkbox";
import Button from "@material-ui/core/Button";
import Avatar from "@material-ui/core/Avatar";
import Assignment from "@material-ui/icons/Assignment";
import Box from "@material-ui/core/Box";
//import IconButton from "@material-ui/core/IconButton";
//import Tooltip from "@material-ui/core/Tooltip";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";
//import DeleteIcon from "@material-ui/icons/Delete";
//import FilterListIcon from "@material-ui/icons/FilterList";
import { listByChannel } from "./api-video";
import { useVideoContext } from "../libs/contextLib";

function createData(videoId, title, playTime, viewCount, publishDate, caption, thumbnailUrl) {
  return { videoId, title, playTime, viewCount, publishDate, caption, thumbnailUrl };
}

function descendingComparator(a, b, orderBy) {
  let aval;
  let bval;
  switch (orderBy) {
    case "playTime":
      const avals = a[orderBy].split(":");
      aval = parseInt(avals[0]) * 60 + parseInt(avals[1]);
      const bvals = b[orderBy].split(":");
      bval = parseInt(bvals[0]) * 60 + parseInt(bvals[1]);
      return bval - aval;
    case "viewCount":
      aval = parseInt(a[orderBy].replace(/,/g, ""), 10);
      bval = parseInt(b[orderBy].replace(/,/g, ""), 10);
      return bval - aval;
    default:
      if (b[orderBy] < a[orderBy]) {
        return -1;
      }
      if (b[orderBy] > a[orderBy]) {
        return 1;
      }
      return 0;
  }
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const headCells = [
  { id: "videoId", numeric: false, disablePadding: true, label: "Video" },
  { id: "title", numeric: false, disablePadding: false, label: "Video Title" },
  { id: "playTime", numeric: false, disablePadding: false, label: "Play Time" },
  { id: "viewCount", numeric: true, disablePadding: false, label: "View Count" },
  { id: "publishDate", numeric: false, disablePadding: false, label: "Published" },
  { id: "caption", numeric: false, disablePadding: false, label: "Caption" },
];

function EnhancedTableHead(props) {
  const { classes, onSelectAllClick, order, orderBy, numSelected, rowCount, onRequestSort } = props;
  const createSortHandler = (colName) => (event) => {
    onRequestSort(event, colName);
  };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? "right" : "left"}
            className={classes.firstCol}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <span className={classes.visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </span>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  classes: PropTypes.object.isRequired,
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};

const useToolbarStyles = makeStyles((theme) => ({
  root: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(1),
  },
  highlight:
    theme.palette.type === "light"
      ? {
          color: theme.palette.secondary.main,
          backgroundColor: lighten(theme.palette.secondary.light, 0.85),
        }
      : {
          color: theme.palette.text.primary,
          backgroundColor: theme.palette.secondary.dark,
        },
  title: {
    flex: "1 1 100%",
    margin: `${theme.spacing(3)}px 0 ${theme.spacing(3)}px ${theme.spacing(1)}px`,
    color: theme.palette.protectedTitle,
    fontSize: "1.2em",
  },
}));

const EnhancedTableToolbar = (props) => {
  const classes = useToolbarStyles();
  const { numSelected, channelTitle, thumbnailUrl } = props;

  return (
    <Toolbar
      className={clsx(classes.root, {
        [classes.highlight]: numSelected > 0,
      })}
    >
      <span className={classes.title} style={{ alignItems: "center", display: "flex" }}>
        <Avatar
          variant="rounded"
          className={classes.rounded}
          src={thumbnailUrl}
          style={{ marginRight: "10px" }}
        >
          <Assignment />
        </Avatar>
        <Typography variant="h6" id="tableTitle">
          {channelTitle}
        </Typography>

        {numSelected > 0 ? (
          <Typography color="inherit" variant="h6" style={{ marginLeft: "10px" }}>
            [{numSelected} selected]
          </Typography>
        ) : (
          " "
        )}
      </span>
    </Toolbar>
  );
};

EnhancedTableToolbar.propTypes = {
  numSelected: PropTypes.number.isRequired,
  channelTitle: PropTypes.string.isRequired,
};

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  paper: {
    width: "100%",
    marginTop: theme.spacing(8),
    marginBottom: theme.spacing(2),
  },
  table: {
    minWidth: 750,
  },
  visuallyHidden: {
    border: 0,
    clip: "rect(0 0 0 0)",
    height: 1,
    margin: -1,
    overflow: "hidden",
    padding: 0,
    position: "absolute",
    top: 20,
    width: 1,
  },
  button: {
    marginLeft: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  firstCol: {
    paddingLeft: theme.spacing(4),
  },
  avatar: theme.avatar,
}));

export default function VideosByChannel() {
  const history = useHistory();
  const classes = useStyles();
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("publishDate");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const { state, dispatch } = useVideoContext();
  if (!state.curChannel) {
    history.push("/");
  }

  useEffect(() => {
    const abortController = new AbortController();
    const signal = abortController.signal;
    console.log("VideosByChannel useEffect state=", state);

    if (state.delaySpeakId) {
      clearTimeout(state.delaySpeakId);
      dispatch({ type: "setDelaySpeakId", payload: undefined });
    }
    
    if (state.sortedVideos.length === 0) {
      listByChannel({ channelId: state.curChannel.channelId }, signal).then((data) => {
        //console.log("MyVideos start data=", data);
        if (data.error) {
          console.log(data.error);
        } else {
          const videoRows = data.map((ro) => {
            return createData(
              ro.videoId,
              ro.videoTitle,
              ro.playTime,
              parseInt(ro.viewCount).toLocaleString(),
              ro.publishDate.indexOf("T") < 0 ? ro.publishDate : ro.publishDate.split("T")[0],
              ro.hasCaption ? "Y" : "N",
              ro.thumbnailUrl
            );
          });
          const sortedRows = stableSort(videoRows, getComparator(order, orderBy));
          //console.log("sortedRows=", sortedRows);
          //setRows(sortedRows);
          dispatch({ type: "setSortedVideos", payload: sortedRows });
        }
      });
    }

    return function cleanup() {
      abortController.abort();
    };
  }, []);

  const handleRequestSort = (event, colName) => {
    console.log("colName=", colName, "  order=", order, "  orderBy=", orderBy);
    const isAsc = orderBy === colName && order === "asc";
    const newOrder = isAsc ? "desc" : "asc";
    const newOrderBy = colName;
    setOrder(newOrder);
    setOrderBy(newOrderBy);
    const sortedRows = stableSort(state.sortedVideos, getComparator(newOrder, newOrderBy));
    //setRows(sortedRows);
    dispatch({ type: "setSortedVideos", payload: sortedRows });
  };

  const handleClick = (video) => (event) => {
    console.log("VideosSelected event=", event);
    console.log("VideosSelected video=", video);
    dispatch({ type: "setVideo", payload: video });
    history.push("/manage/video/");
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const isSelected = (videoId) => state.selected.indexOf(videoId) !== -1;
  const emptyRows =
    rowsPerPage - Math.min(rowsPerPage, state.sortedVideos.length - page * rowsPerPage);

  return (
    <div className={classes.root}>
      <Paper className={classes.paper} elevation={4}>
        <EnhancedTableToolbar
          className={classes.title}
          numSelected={state.selected.length}
          channelTitle={state.curChannel.channelTitle}
          thumbnailUrl={state.curChannel.thumbnailUrl}
        />
        <TableContainer>
          <Table
            className={classes.table}
            aria-labelledby="tableTitle"
            size="small"
            aria-label="enhanced table"
          >
            <EnhancedTableHead
              classes={classes}
              numSelected={state.selected.length}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              rowCount={state.sortedVideos.length}
            />
            <TableBody>
              {state.sortedVideos
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, index) => {
                  const isItemSelected = isSelected(row.videoId);
                  const labelId = `enhanced-table-checkbox-${index}`;

                  return (
                    <TableRow
                      hover
                      onClick={handleClick(row)}
                      key={row.videoId}
                    >
                      <TableCell component="th" id={labelId} scope="row" className={classes.firstCol}>
                        <span style={{ alignItems: "center", display: "flex" }}>
                          <Avatar
                            variant="rounded"
                            className={classes.rounded, classes.avatar}
                            src={row.thumbnailUrl}
                            style={{ marginRight: "10px" }}
                          >
                            <Assignment />
                          </Avatar>
                          {/*row.videoId*/}
                        </span>
                      </TableCell>
                      <TableCell align="left">{row.title}</TableCell>
                      <TableCell align="left">{row.playTime}</TableCell>
                      <TableCell align="right">{row.viewCount}</TableCell>
                      <TableCell align="left">{row.publishDate}</TableCell>
                      <TableCell align="left">{row.caption}</TableCell>
                    </TableRow>
                  );
                })}
              {emptyRows > 0 && (
                <TableRow style={{ height: 33 * emptyRows }}>
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 20, 30]}
          component="div"
          count={state.sortedVideos.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </Paper>
    </div>
  );
}
